import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { Box, Typography, TextField, Button, Stack, Alert, Paper } from "@mui/material";
import { notifications } from "@mantine/notifications";
import { IconDeviceFloppy, IconTrash, IconArrowLeft, IconFileExport } from "@tabler/icons-react";
import { PageContainer } from "../components/Container/PageContainer";
import { useUser } from "../contexts/UserContext";
import {
  getIdeation,
  updateIdeation,
  deleteIdeation,
  exportIdeationToDraft,
} from "../utils/ideationUtils";
import { useTranslation } from "react-i18next";

const IdeationEdit = () => {
  const { t } = useTranslation();
  const { id } = useParams();
  const { profile } = useUser();
  const navigate = useNavigate();

  const [ideation, setIdeation] = useState(null);
  const [text, setText] = useState("");
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [exporting, setExporting] = useState(false);

  // 아이디어 로드
  useEffect(() => {
    const loadIdeation = async () => {
      if (!profile?.id || !id) return;

      try {
        setLoading(true);
        const data = await getIdeation(profile.id, id);
        setIdeation(data);
        setText(data.text || "");
      } catch (error) {
        console.error('[IdeationEdit] Failed to load ideation:', error);
        notifications.show({
          title: '오류',
          message: error.message || '아이디어를 불러오는데 실패했습니다.',
          color: 'red',
        });
        navigate('/ideation');
      } finally {
        setLoading(false);
      }
    };

    loadIdeation();
  }, [profile?.id, id, navigate]);

  // 저장
  const handleSave = async () => {
    if (!text.trim()) {
      notifications.show({
        title: '오류',
        message: '내용을 입력해주세요.',
        color: 'red',
      });
      return;
    }

    try {
      setSaving(true);
      await updateIdeation(profile.id, id, text);
      notifications.show({
        title: '저장 완료',
        message: '아이디어가 저장되었습니다.',
        color: 'green',
      });
      setIdeation((prev) => ({ ...prev, text }));
    } catch (error) {
      console.error('[IdeationEdit] Save failed:', error);
      notifications.show({
        title: '저장 실패',
        message: error.message || '아이디어 저장에 실패했습니다.',
        color: 'red',
      });
    } finally {
      setSaving(false);
    }
  };

  // 임시글로 내보내기
  const handleExportToDraft = async () => {
    if (!text.trim()) {
      notifications.show({
        title: '오류',
        message: '내용을 입력해주세요.',
        color: 'red',
      });
      return;
    }

    if (!window.confirm('이 아이디어를 임시글로 내보내시겠습니까?')) return;

    try {
      setExporting(true);

      // exportIdeationToDraft는 saveDraft를 호출하여 draft 생성
      const draftId = await exportIdeationToDraft(profile.id, id, text);

      notifications.show({
        title: '내보내기 완료',
        message: '임시글로 내보내기가 완료되었습니다.',
        color: 'green',
      });

      // PostThreads의 draft 편집 페이지로 이동
      navigate(`/post-thread/draft/${draftId}`);
    } catch (error) {
      console.error('[IdeationEdit] Export failed:', error);
      notifications.show({
        title: '내보내기 실패',
        message: error.message || '임시글로 내보내기에 실패했습니다.',
        color: 'red',
      });
    } finally {
      setExporting(false);
    }
  };

  // 삭제
  const handleDelete = async () => {
    if (!window.confirm('이 아이디어를 삭제하시겠습니까?')) return;

    try {
      await deleteIdeation(profile.id, id);
      notifications.show({
        title: '삭제 완료',
        message: '아이디어가 삭제되었습니다.',
        color: 'green',
      });
      navigate('/ideation');
    } catch (error) {
      console.error('[IdeationEdit] Delete failed:', error);
      notifications.show({
        title: '삭제 실패',
        message: error.message || '아이디어 삭제에 실패했습니다.',
        color: 'red',
      });
    }
  };

  // 목록으로
  const handleBackToList = () => {
    navigate('/ideation');
  };

  if (loading) {
    return (
      <PageContainer expand={true}>
        <Box sx={{ maxWidth: 800, margin: '0 auto', p: { xs: 2, md: 3 } }}>
          <Typography>로딩 중...</Typography>
        </Box>
      </PageContainer>
    );
  }

  if (!ideation) {
    return (
      <PageContainer expand={true}>
        <Box sx={{ maxWidth: 800, margin: '0 auto', p: { xs: 2, md: 3 } }}>
          <Alert severity="error">아이디어를 찾을 수 없습니다.</Alert>
        </Box>
      </PageContainer>
    );
  }

  const isChanged = text !== ideation.text;

  return (
    <PageContainer expand={true}>
      <Box sx={{ maxWidth: 800, margin: '0 auto', p: { xs: 2, md: 3 } }}>
        {/* 헤더 */}
        <Stack spacing={2} sx={{ mb: 4 }}>
          <Button
            startIcon={<IconArrowLeft size={20} />}
            onClick={handleBackToList}
            sx={{ alignSelf: 'flex-start', textTransform: 'none' }}
          >
            목록으로
          </Button>
          <Typography variant="h4" sx={{ fontWeight: 700 }}>
            아이디어 수정
          </Typography>
        </Stack>

        {/* 편집 영역 */}
        <Paper elevation={0} sx={{ p: 3, mb: 3, border: '1px solid', borderColor: 'divider' }}>
          <TextField
            fullWidth
            multiline
            minRows={10}
            maxRows={20}
            value={text}
            onChange={(e) => setText(e.target.value)}
            placeholder="아이디어 내용을 입력하세요..."
            variant="outlined"
            sx={{
              '& .MuiOutlinedInput-root': {
                fontFamily: 'inherit',
                fontSize: '1rem',
                lineHeight: 1.6,
              },
            }}
          />
          <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
            {text.length} 자
          </Typography>
        </Paper>

        {/* 액션 버튼 */}
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
          <Button
            variant="contained"
            startIcon={<IconDeviceFloppy size={20} />}
            onClick={handleSave}
            disabled={saving || !isChanged || !text.trim()}
            sx={{ textTransform: 'none', flex: 1 }}
          >
            {saving ? '저장 중...' : '저장'}
          </Button>

          <Button
            variant="contained"
            color="success"
            startIcon={<IconFileExport size={20} />}
            onClick={handleExportToDraft}
            disabled={exporting || !text.trim()}
            sx={{ textTransform: 'none', flex: 1 }}
          >
            {exporting ? '내보내는 중...' : '임시글로 내보내기'}
          </Button>

          <Button
            variant="outlined"
            color="error"
            startIcon={<IconTrash size={20} />}
            onClick={handleDelete}
            disabled={saving || exporting}
            sx={{ textTransform: 'none', minWidth: { xs: 'auto', sm: 120 } }}
          >
            삭제
          </Button>
        </Stack>

        {/* 안내 메시지 */}
        <Alert severity="info" sx={{ mt: 3 }}>
          <Typography variant="body2">
            <strong>임시글로 내보내기:</strong> 이 아이디어를 PostThreads의 임시글로 변환합니다.
            임시글에서 추가 편집, 예약 게시 또는 즉시 게시할 수 있습니다.
          </Typography>
        </Alert>
      </Box>
    </PageContainer>
  );
};

export default IdeationEdit;
